import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEquipo } from '../context/EquipoContext';
import { useAuth }   from '../context/AuthContext';

import RegistroResultado from '../components/postpartido/RegistroResultado';
import EvaluacionRival   from '../components/postpartido/EvaluacionRival';
import ResumenElo        from '../components/postpartido/ResumenElo';
import { getDesafiosEquipo, getEquipos, registrarPartido, crearEvaluacion, actualizarEstadoDesafio, getPartidosEquipo } from '../services/api';

function calcularNuevoElo(eloJugador, eloRival, resultado) {
  const K        = 32;
  const esperado = 1 / (1 + Math.pow(10, (eloRival - eloJugador) / 400));
  return Math.round(eloJugador + K * (resultado - esperado));
}

function getIniciales(nombre) {
  if (!nombre) return '??';
  return nombre.split(' ').filter(p => p.length > 0).slice(0,2).map(p => p[0].toUpperCase()).join('');
}

// FIX: formatear fecha correctamente
function formatFecha(fechaStr) {
  if (!fechaStr) return '';
  try {
    // Tomar solo la parte YYYY-MM-DD para evitar problemas de zona horaria
    const solo = String(fechaStr).slice(0, 10);
    const [y, m, d] = solo.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('es-CL', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return String(fechaStr).slice(0, 10); }
}

const ETAPAS = [
  { num: 1, label: 'Resultado'  },
  { num: 2, label: 'Evaluación' },
  { num: 3, label: 'ELO'        },
];

export default function PostPartido() {
  const navigate                          = useNavigate();
  const location                          = useLocation();
  const { equipo, actualizarEquipoEnContexto, deporteActivo } = useEquipo();
  const { token }                         = useAuth();

  const [etapa,           setEtapa]           = useState(1);
  const [resultado,       setResultado]       = useState(null);
  const [evaluacion,      setEvaluacion]      = useState(null);
  const [eloAntes,        setEloAntes]        = useState(null);
  const [eloDespues,      setEloDespues]      = useState(null);
  const [guardando,       setGuardando]       = useState(false);
  const [error,           setError]           = useState('');
  const [partidoExistente, setPartidoExistente] = useState(null);

  const [desafioActual, setDesafioActual] = useState(location.state?.desafio || null);
  const [equipos,       setEquipos]       = useState([]);
  const [desafios,      setDesafios]      = useState([]);
  const [seleccionando, setSeleccionando] = useState(!location.state?.desafio);
  const [deporteDesafioFetch, setDeporteDesafioFetch] = useState(
    location.state?.desafio?.deporte || null
  );

  const soloEvaluar    = location.state?.soloEvaluar || false;
  const deporteDesafio = deporteDesafioFetch || desafioActual?.deporte || deporteActivo || 'futbol';
  const esFutbol       = !deporteDesafio || deporteDesafio === 'futbol';

  useEffect(() => {
    if (!equipo?.id || !token) return;
    const desafioId = location.state?.desafio?.id || location.state?.desafio?.desafio_id;
    if (!desafioId) return;
    // Buscar deporte del desafío directamente
    getDesafiosEquipo(equipo.id, token).then(data => {
      if (Array.isArray(data)) {
        const d = data.find(x => x.id === desafioId);
        if (d?.deporte) setDeporteDesafioFetch(d.deporte);
      }
    }).catch(() => {});
  }, [equipo?.id, token]);

  useEffect(() => {
    if (!equipo?.id || !token) return;
    Promise.all([
      getDesafiosEquipo(equipo.id, token),
      getEquipos(),
      getPartidosEquipo(equipo.id, token),
    ]).then(async ([desafiosData, equiposData, partidosData]) => {
      // Si el desafio no trae deporte, completarlo
      if (desafioActual && !desafioActual.deporte) {
        const allDesafios = Array.isArray(desafiosData) ? desafiosData : [];
        const completo = allDesafios.find(d => d.id === desafioActual.id);
        if (completo?.deporte) {
          setDesafioActual(prev => ({ ...prev, deporte: completo.deporte }));
        }
      }
      if (Array.isArray(desafiosData)) {
        // FIX: excluir 'jugado' — solo mostrar confirmados pendientes de registrar
        const confirmados = desafiosData.filter(d =>
          (d.equipo_local_id === equipo.id || d.equipo_visitante_id === equipo.id) &&
          (d.estado === 'confirmado')
        );
        setDesafios(confirmados);
        if (confirmados.length === 1 && !desafioActual) {
          setDesafioActual(confirmados[0]);
          setSeleccionando(false);
        }
      }
      if (Array.isArray(equiposData)) {
        setEquipos(equiposData);
        const miEquipoActual = equiposData.find(e => e.id === equipo.id);
        if (miEquipoActual) {
          const campoElo = esFutbol ? 'elo' : deporteActivo === 'padel' ? 'elo_padel' : 'elo_tenis';
          setEloAntes(miEquipoActual[campoElo] || 1000);
          actualizarEquipoEnContexto(miEquipoActual);
        }
      }
      // Detectar si el partido ya fue registrado por el otro equipo
      if (Array.isArray(partidosData) && location.state?.desafio) {
        const desafioId = location.state.desafio.desafio_id || location.state.desafio.id;
        const pExistente = partidosData.find(p => p.desafio_id === desafioId);
        if (pExistente) {
          setPartidoExistente(pExistente);
          if (!soloEvaluar) setEtapa(2);
        }
      }
    }).catch(() => {});

    if (soloEvaluar && location.state?.desafio) {
      setDesafioActual(prev => prev || location.state.desafio);
      setSeleccionando(false);
      setEtapa(2);
    }
  }, [equipo, token]);

  const getNombreEquipo = (id) => {
    const e = equipos.find(eq => eq.id === id);
    return e?.nombre || 'Equipo #' + id;
  };

  const getRivalId = (d) => d?.equipo_local_id === equipo?.id
    ? d?.equipo_visitante_id
    : d?.equipo_local_id;

  const rivalId     = desafioActual ? getRivalId(desafioActual) : null;
  const rivalEquipo = equipos.find(e => e.id === rivalId);
  const campoEloRival = esFutbol ? 'elo' : deporteActivo === 'padel' ? 'elo_padel' : 'elo_tenis';
  const rivalElo    = rivalEquipo?.[campoEloRival] || 1000;
  const rivalNombre = rivalEquipo?.nombre || (rivalId ? getNombreEquipo(rivalId) : 'Rival');

  const partidoParaComponentes = {
    rival:           rivalNombre,
    inicialesLocal:  getIniciales(equipo?.nombre || ''),
    inicialesRival:  getIniciales(rivalNombre),
    colorPrincipal:  equipo?.colorPrincipal  || '#FF6B1A',
    colorSecundario: equipo?.colorSecundario || '#1A6BFF',
    color1Rival:     '#1A6BFF',
    color2Rival:     '#0F4FCC',
    eloRival:        rivalElo,
    local:           equipo?.nombre || 'Mi equipo',
  };

  const handleResultado = (datos) => {
    setResultado(datos);
    const resultadoElo = datos.ganamos ? 1 : datos.empate ? 0.5 : 0;
    const campoElo = esFutbol ? 'elo' : deporteActivo === 'padel' ? 'elo_padel' : 'elo_tenis';
    const eloActual = equipo?.[campoElo] || 1000;
    const nuevoElo = calcularNuevoElo(eloActual, rivalElo, resultadoElo);
    setEloAntes(eloActual);
    setEloDespues(nuevoElo);
    setEtapa(2);
  };

  const handleEvaluacion = (datos) => {
    setEvaluacion(datos);
    if (partidoExistente || soloEvaluar) {
      handleFinalizarConEvaluacion(datos);
    } else {
      setEtapa(3);
    }
  };

  const handleFinalizarConEvaluacion = async (evalDatos) => {
    if (!desafioActual) { navigate('/app/partidos'); return; }
    setGuardando(true); setError('');
    try {
      const partidoId = partidoExistente?.id || await (async () => {
        const partidos = await getPartidosEquipo(equipo.id, token);
        const p = partidos.find(p =>
          p.desafio_id === desafioActual.id ||
          p.desafio_id === desafioActual.desafio_id
        );
        return p?.id;
      })();

      if (evalDatos && partidoId) {
        await crearEvaluacion({
          partido_id:     partidoId,
          equipo_origen:  equipo.id,
          equipo_destino: rivalId,
          puntualidad:    evalDatos.evaluaciones?.puntualidad  || evalDatos.puntualidad  || 5,
          fairplay:       evalDatos.evaluaciones?.fairplay     || evalDatos.fairplay     || 5,
          ambiente:       evalDatos.evaluaciones?.ambiente     || evalDatos.ambiente     || 5,
          organizacion:   evalDatos.evaluaciones?.organizacion || evalDatos.organizacion || 5,
          comentario:     evalDatos.comentario || '',
        }, token);
      }
      navigate('/app/partidos');
    } catch {
      setError('Error al guardar. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const handleFinalizar = async () => {
    if (!desafioActual) { navigate('/app/partidos'); return; }
    setGuardando(true); setError('');
    try {
      let partidoId = null;

      if (partidoExistente) {
        partidoId = partidoExistente.id;
      } else if (!soloEvaluar) {
        const esLocal = desafioActual.equipo_local_id === equipo?.id;
        const resultadoElo = resultado?.ganamos ? 1 : resultado?.empate ? 0.5 : 0;
        const nuevoEloRival = calcularNuevoElo(rivalElo, equipo?.elo || 800, 1 - resultadoElo);

        const dataPartido = await registrarPartido({
          desafio_id:             desafioActual.id,
          goles_local:            esLocal ? (resultado?.golesLocal || 0) : (resultado?.golesVisitante || 0),
          goles_visitante:        esLocal ? (resultado?.golesVisitante || 0) : (resultado?.golesLocal || 0),
          resultado_local:        esLocal ? (resultado?.golesLocal || 0) : (resultado?.golesVisitante || 0),
          resultado_visitante:    esLocal ? (resultado?.golesVisitante || 0) : (resultado?.golesLocal || 0),
          elo_local_antes:        esLocal ? eloAntes : rivalElo,
          elo_local_despues:      esLocal ? eloDespues : nuevoEloRival,
          elo_visitante_antes:    esLocal ? rivalElo : eloAntes,
          elo_visitante_despues:  esLocal ? nuevoEloRival : eloDespues,
          resultado_detalle:      resultado?.resultado_detalle || null,
        }, token);

        if (dataPartido.error) { setError('Error al registrar partido: ' + dataPartido.error); return; }
        partidoId = dataPartido.id;

        const equiposActualizados = await getEquipos();
        if (Array.isArray(equiposActualizados)) {
          const miEquipoActualizado = equiposActualizados.find(e => e.id === equipo.id);
          if (miEquipoActualizado) actualizarEquipoEnContexto(miEquipoActualizado);
        }

        await actualizarEstadoDesafio(desafioActual.id, 'jugado', token);
      } else {
        const partidos = await getPartidosEquipo(equipo.id, token);
        const p = partidos.find(p => p.desafio_id === desafioActual.id || p.desafio_id === desafioActual.desafio_id);
        partidoId = p?.id;
      }

      if (evaluacion && partidoId) {
        await crearEvaluacion({
          partido_id:     partidoId,
          equipo_origen:  equipo.id,
          equipo_destino: rivalId,
          puntualidad:    evaluacion.evaluaciones?.puntualidad  || evaluacion.puntualidad  || 5,
          fairplay:       evaluacion.evaluaciones?.fairplay     || evaluacion.fairplay     || 5,
          ambiente:       evaluacion.evaluaciones?.ambiente     || evaluacion.ambiente     || 5,
          organizacion:   evaluacion.evaluaciones?.organizacion || evaluacion.organizacion || 5,
          comentario:     evaluacion.comentario || '',
        }, token);
      }

      navigate('/app/partidos');
    } catch {
      setError('Error al guardar. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  // ── SELECCIÓN DE PARTIDO ──
  if (seleccionando) {
    return (
      <div style={styles.pagina}>
        <div style={styles.container}>
          <div style={styles.logo}>
            Tu<span style={styles.logoSpan}>Rival</span>
            <span style={styles.logoSub}> · Post partido</span>
          </div>

          <div style={styles.card}>
            <div style={styles.selTitulo}>¿Qué partido vas a registrar?</div>
            <div style={styles.selSub}>
              Selecciona el partido confirmado que ya se jugó.
            </div>

            {desafios.length === 0 ? (
              <div style={styles.vacio}>
                <div style={styles.vacioIcono}>📅</div>
                <div style={styles.vacioTitulo}>Sin partidos confirmados</div>
                <div style={styles.vacioSub}>
                  Acepta un desafío primero para poder registrar un resultado.
                </div>
                <button
                  style={styles.btnVolver}
                  onClick={() => navigate('/app/partidos')}
                >
                  Ver mis partidos →
                </button>
              </div>
            ) : (
              <div style={styles.desafiosList}>
                {desafios.map(d => {
                  const rId    = getRivalId(d);
                  const nombre = getNombreEquipo(rId);
                  return (
                    <div
                      key={d.id}
                      style={styles.desafioSelCard}
                      onClick={() => { setDesafioActual(d); setSeleccionando(false); }}
                    >
                      <div style={styles.desafioSelInfo}>
                        <div style={styles.desafioSelNombre}>{nombre}</div>
                        <div style={styles.desafioSelFecha}>
                          📅 {formatFecha(d.fecha)} · {d.hora}
                        </div>
                      </div>
                      <div style={styles.desafioSelArrow}>→</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pagina}>
      <div style={styles.container}>

        <div style={styles.logo}>
          Tu<span style={styles.logoSpan}>Rival</span>
          <span style={styles.logoSub}> · Post partido</span>
        </div>

        {/* Info del partido — FIX: fecha formateada */}
        <div style={styles.partidoInfo}>
          vs {rivalNombre}
          {desafioActual?.fecha && ` · ${formatFecha(desafioActual.fecha)}`}
          {desafioActual?.hora  && ` · ${desafioActual.hora}`}
        </div>

        {/* Stepper */}
        <div style={styles.stepper}>
          {ETAPAS.map(({ num, label }, i) => {
            const completado = etapa > num;
            const activo     = etapa === num;
            return (
              <div key={num} style={styles.stepItem}>
                <div style={{
                  ...styles.stepCirculo,
                  background: completado ? '#16A34A' : activo ? '#FF6B1A' : 'rgba(255,255,255,0.1)',
                  border: activo ? '2px solid #FF6B1A' : completado ? '2px solid #16A34A' : '2px solid rgba(255,255,255,0.15)',
                }}>
                  <span style={{
                    color: completado || activo ? 'white' : 'rgba(255,255,255,0.3)',
                    fontSize: 13, fontWeight: 700,
                    fontFamily: "'Barlow Condensed', sans-serif",
                  }}>
                    {completado ? '✓' : num}
                  </span>
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 600,
                  color: activo ? 'white' : 'rgba(255,255,255,0.3)',
                  fontFamily: "'Barlow', sans-serif",
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>
                  {label}
                </span>
                {i < ETAPAS.length - 1 && (
                  <div style={{
                    ...styles.stepLinea,
                    background: completado ? '#16A34A' : 'rgba(255,255,255,0.1)',
                  }} />
                )}
              </div>
            );
          })}
        </div>

        <div style={styles.card}>
          {error && <div style={styles.errorBanner}>{error}</div>}

          {/* Mostrar resultado ya registrado cuando viene del otro equipo */}
          {partidoExistente && desafioActual && etapa === 2 && (
            <div style={{
              background: '#F0FDF4', border: '1.5px solid #86EFAC',
              borderRadius: 12, padding: '14px 16px', marginBottom: 16,
              textAlign: 'center',
            }}>
              <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 13, fontWeight: 700, color: '#16A34A', marginBottom: 6 }}>
                ✅ Resultado registrado
              </div>
              <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 22, fontWeight: 900, color: '#141922' }}>
                {(() => {
                  const esLocal = desafioActual.equipo_local_id === equipo?.id;
                  if (!esFutbol && partidoExistente.resultado_detalle) {
                    try {
                      const det = typeof partidoExistente.resultado_detalle === 'string'
                        ? JSON.parse(partidoExistente.resultado_detalle)
                        : partidoExistente.resultado_detalle;
                      if (det?.sets) return det.sets.map(s => `${s.local}-${s.visitante}`).join(', ');
                    } catch {}
                  }
                  const gP = esLocal ? (partidoExistente.goles_local ?? '?') : (partidoExistente.goles_visitante ?? '?');
                  const gR = esLocal ? (partidoExistente.goles_visitante ?? '?') : (partidoExistente.goles_local ?? '?');
                  return `${gP} — ${gR}`;
                })()}
              </div>
              <div style={{ fontSize: 12, color: '#6B7280', fontFamily: "'Barlow', sans-serif", marginTop: 4 }}>
                {equipo?.nombre} vs {rivalNombre}
              </div>
            </div>
          )}

          {etapa === 1 && (
            <RegistroResultado
              partido={{ ...partidoParaComponentes, deporte: desafioActual?.deporte || deporteActivo }}
              onNext={handleResultado}
              onCancelar={() => navigate('/app/partidos')}
            />
          )}
          {etapa === 2 && (
            <EvaluacionRival
              partido={partidoParaComponentes}
              resultado={resultado}
              onNext={handleEvaluacion}
              onBack={() => setEtapa(1)}
            />
          )}
          {etapa === 3 && (
            <ResumenElo
              partido={partidoParaComponentes}
              resultado={resultado}
              evaluacion={evaluacion}
              eloAntes={eloAntes}
              eloDespues={eloDespues}
              onFinalizar={handleFinalizar}
              guardando={guardando}
              soloEvaluar={soloEvaluar}
            />
          )}
        </div>

      </div>
    </div>
  );
}

const styles = {
  pagina: {
    minHeight: '100vh', background: '#0D1117',
    padding: '32px 20px',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  },
  container: { width: '100%', maxWidth: 580 },
  logo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 28, fontWeight: 800, color: 'white',
    textAlign: 'center', marginBottom: 16, letterSpacing: '-0.02em',
  },
  logoSpan: { color: '#FF6B1A' },
  logoSub:  { fontSize: 16, fontWeight: 500, color: 'rgba(255,255,255,0.3)' },
  partidoInfo: {
    textAlign: 'center', marginBottom: 20,
    fontSize: 14, color: 'rgba(255,255,255,0.4)',
    fontFamily: "'Barlow', sans-serif",
  },
  stepper: {
    display: 'flex', alignItems: 'flex-start',
    justifyContent: 'center',
    marginBottom: 24, padding: '0 40px',
  },
  stepItem: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: 5,
    position: 'relative', flex: 1,
  },
  stepCirculo: {
    width: 34, height: 34, borderRadius: '50%',
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', zIndex: 1, transition: 'all 0.2s',
  },
  stepLinea: {
    position: 'absolute', top: 17, left: '50%',
    width: '100%', height: 2, zIndex: 0, transition: 'background 0.2s',
  },
  card: {
    background: 'white', borderRadius: 20, padding: '32px',
  },
  errorBanner: {
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid #FCA5A5',
    borderRadius: 10, padding: '10px 14px',
    fontSize: 13, color: '#EF4444',
    fontFamily: "'Barlow', sans-serif",
    marginBottom: 16,
  },
  selTitulo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 22, fontWeight: 800, color: '#141922', marginBottom: 6,
  },
  selSub: {
    fontSize: 13, color: '#9BA5B7',
    fontFamily: "'Barlow', sans-serif", marginBottom: 20,
  },
  vacio: { textAlign: 'center', padding: '20px 0' },
  vacioIcono:  { fontSize: 40, marginBottom: 12 },
  vacioTitulo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 20, fontWeight: 700, color: '#141922', marginBottom: 6,
  },
  vacioSub: {
    fontSize: 13, color: '#9BA5B7',
    fontFamily: "'Barlow', sans-serif",
    maxWidth: 300, margin: '0 auto 16px', lineHeight: 1.5,
  },
  btnVolver: {
    background: '#FF6B1A', color: 'white', border: 'none',
    borderRadius: 10, padding: '10px 20px', cursor: 'pointer',
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 14, fontWeight: 700,
  },
  desafiosList: { display: 'flex', flexDirection: 'column', gap: 8 },
  desafioSelCard: {
    display: 'flex', alignItems: 'center',
    background: '#F4F6FA', border: '1.5px solid #E8ECF4',
    borderRadius: 12, padding: '14px 16px',
    cursor: 'pointer', transition: 'all 0.12s',
  },
  desafioSelInfo: { flex: 1 },
  desafioSelNombre: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 16, fontWeight: 700, color: '#141922', marginBottom: 3,
  },
  desafioSelFecha: {
    fontSize: 12, color: '#6B7280',
    fontFamily: "'Barlow', sans-serif",
  },
  desafioSelArrow: {
    fontSize: 18, color: '#9BA5B7',
    fontFamily: "'Barlow', sans-serif",
  },
};
